import { Alert, Box, Flex, Image, Text, useColorModeValue } from '@chakra-ui/react';
import React from 'react';

import { route } from 'nextjs-routes';

import useApiQuery from 'lib/api/useApiQuery';
import { AddressHighlightProvider } from 'lib/contexts/addressHighlight';
import useNewTxsSocket from 'lib/hooks/useNewTxsSocket';
import { useI18n } from 'lib/i18n/context';
import { getSocketNoticeKey } from 'lib/i18n/socketNotice';
import { TX } from 'stubs/tx';
import LinkInternal from 'ui/shared/links/LinkInternal';

import LatestTxsItem from './LatestTxsItem';
import LatestTxsItemMobile from './LatestTxsItemMobile';

type Props = {
  variant?: 'default' | 'tabbed';
};

const LatestTransactions = ({ variant = 'default' }: Props) => {
  const cardBg = useColorModeValue('#FFFFFF', '#111111');
  const borderColor = useColorModeValue('#E9ECEF', '#222222');
  const titleColor = useColorModeValue('#616161', '#999999');
  const valueColor = useColorModeValue('#000000', '#FFFFFF');
  const noticeBg = useColorModeValue(
    'linear-gradient(90deg, rgba(20, 220, 172, 1) 0%, rgba(255, 255, 255, 1) 100%)',
    'linear-gradient(90deg, rgba(20, 220, 172, 1) 0%, rgba(20, 20, 20, 1) 100%)',
  );
  const arrowSrc = useColorModeValue('/static/homepage/latest-blocks-arrow-light.svg', '/static/homepage/latest-blocks-arrow.svg');
  const { t } = useI18n();
  const txsCount = 6;
  const { data, isPlaceholderData, isError } = useApiQuery('homepage_txs', {
    queryOptions: {
      placeholderData: Array(txsCount).fill(TX),
    },
  });

  const { num, socketAlert } = useNewTxsSocket();
  const txsUrl = route({ pathname: '/txs' });
  const showCardShell = variant === 'default';
  const showNotice = Boolean(socketAlert) || Boolean(num);

  const noticeContent = socketAlert || (
    <>
      <LinkInternal
        href={ txsUrl }
        color={ valueColor }
        _hover={{ color: valueColor, textDecoration: 'none' }}
      >
        { num ? t(getSocketNoticeKey('transaction', num), { count: num.toLocaleString() }) : null }
      </LinkInternal>
      <Text as="span" color={ valueColor }> { t('common.socketNotice.haveComeIn') }</Text>
    </>
  );

  if (isError) {
    return <Text mt={ 4 } color={ titleColor }>{ t('home.noDataPleaseReloadThePage') }</Text>;
  }

  if (!data) {
    return null;
  }

  const bodyContent = (
    <>
      <Flex flexDirection="column" display={{ base: 'flex', lg: 'none' }}>
        { data.slice(0, txsCount).map((tx, index) => (
          <LatestTxsItemMobile
            key={ tx.hash + (isPlaceholderData ? index : '') }
            tx={ tx }
            isLoading={ isPlaceholderData }
          />
        )) }
      </Flex>
      <AddressHighlightProvider>
        <Flex flexDirection="column" display={{ base: 'none', lg: 'flex' }}>
          { data.slice(0, txsCount).map((tx, index, items) => (
            <LatestTxsItem
              key={ tx.hash + (isPlaceholderData ? index : '') }
              tx={ tx }
              isLoading={ isPlaceholderData }
              isLast={ index === items.length - 1 }
            />
          )) }
        </Flex>
      </AddressHighlightProvider>
    </>
  );

  if (!showCardShell) {
    return (
      <Box>
        { bodyContent }
        <Flex justifyContent="center" pt="16px">
          <LinkInternal fontSize="sm" href={ txsUrl }>{ t('home.viewAllTransactions') }</LinkInternal>
        </Flex>
      </Box>
    );
  }

  return (
    <Box bg={ cardBg } border="1px solid" borderColor={ borderColor } borderRadius="8px" overflow="hidden">
      <Box px="16px" py="16px" minH="74px" borderBottom="1px solid" borderColor={ borderColor }>
        <Flex justifyContent="space-between" alignItems="center">
          <Text
            color={ titleColor }
            fontSize="16px"
            lineHeight="20.42px"
            fontWeight={ 500 }
            fontFamily="'Space Grotesk', var(--chakra-fonts-body)"
          >
            { t('home.latestTransactions') }
          </Text>
          <LinkInternal
            href={ txsUrl }
            display="inline-flex"
            alignItems="center"
            gap="4px"
            color={ valueColor }
            fontSize="14px"
            lineHeight="24px"
            fontWeight={ 500 }
            fontFamily="'Space Grotesk', var(--chakra-fonts-body)"
            _hover={{ color: valueColor, textDecoration: 'none' }}
          >
            <span>{ t('home.viewAllTransactions') }</span>
            <Image src={ arrowSrc } alt="" boxSize="16px" flexShrink={ 0 }/>
          </LinkInternal>
        </Flex>
        { showNotice ? (
          <Alert
            mt="8px"
            h="22px"
            px="16px"
            py="3px"
            borderRadius="0"
            bg={ noticeBg }
            color={ valueColor }
            fontWeight={ 400 }
            fontSize="14px"
            lineHeight="20px"
            fontFamily="'Space Grotesk', var(--chakra-fonts-body)"
          >
            { noticeContent }
          </Alert>
        ) : null }
      </Box>
      <Box>
        { bodyContent }
      </Box>
    </Box>
  );
};

export default LatestTransactions;
